/**
 * 수치 추세 차트
 * ---------------------------------------------------------------------------
 * ## 값은 서버가 준다
 * 차트는 숫자가 틀리면 그림까지 틀린다. 그래서 **AI 에게 그릴 값을 묻지
 * 않는다.** 서버가 운영일지 원장에서 뽑아 계산한 계열을 그대로 그린다
 * (AiReportBuilder 의 SeriesCollector). 본문의 표와 같은 숫자다.
 *
 * ## 묶어서 그린다
 * 폭기조 pH·DO·MLSS 를 한 차트에 겹치면 MLSS(수천) 때문에 pH(7 언저리)가
 * 바닥에 붙어 선이 보이지 않는다. **값의 크기가 다른 것은 겹치지 않는다** —
 * 계열마다 작은 차트를 하나씩 두고, 같은 묶음끼리 나란히 놓는다.
 *
 * ## 인쇄·PDF
 * recharts 는 SVG 를 그린다. 캡처할 때 애니메이션이 돌고 있으면 빈 그림이
 * 찍히므로 `isAnimationActive={false}` 로 끈다. 반응형(ResponsiveContainer)도
 * 캡처 시점에 너비가 0 일 수 있어 **고정 크기**로 그린다.
 */

import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Empty, Tag, Typography } from 'antd';

const { Text } = Typography;

export interface TrendSeries {
  key: string;
  label: string;
  unit: string;
  group: string;
  points: Array<{ date: string; value: number }>;
  first: number;
  last: number;
  change: number;
  changePct: number | null;
  min: number;
  max: number;
  avg: number;
  direction: 'up' | 'down' | 'flat';
}

/** 캡처(PDF) 중에는 반응형을 끄고 고정 너비로 그린다. */
export function ReportCharts({
  series,
  fixedWidth,
}: {
  series: TrendSeries[];
  fixedWidth?: number;
}) {
  if (!series || series.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="비교할 날이 부족해 추세를 그리지 못했습니다"
      />
    );
  }

  /* 묶음별로 모은다 — 대기 / 폐수 위탁 / 폭기조 / 수질 … */
  const groups = new Map<string, TrendSeries[]>();
  for (const s of series) {
    const list = groups.get(s.group);
    if (list) list.push(s);
    else groups.set(s.group, [s]);
  }

  return (
    <div className="eco-charts">
      {[...groups.entries()].map(([group, list]) => (
        <div key={group} className="eco-chart-group">
          <div className="eco-chart-group-title">{group}</div>
          <div className="eco-chart-row">
            {list.map((s) => (
              <ChartCard key={s.key} s={s} fixedWidth={fixedWidth} />
            ))}
          </div>
        </div>
      ))}
      <style>{CSS}</style>
    </div>
  );
}

function ChartCard({ s, fixedWidth }: { s: TrendSeries; fixedWidth?: number }) {
  const color = s.direction === 'up' ? '#cf1322' : s.direction === 'down' ? '#1677ff' : '#8c8c8c';
  const arrow = s.direction === 'up' ? '▲' : s.direction === 'down' ? '▼' : '—';
  const unit = s.unit ? s.unit : '';

  const chart = (
    <LineChart data={s.points} margin={{ top: 6, right: 10, bottom: 2, left: -18 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
      <XAxis
        dataKey="date"
        tick={{ fontSize: 10, fill: '#8c8c8c' }}
        /* 2026-10-21 → 10-21. 좁은 차트에 연도까지 넣으면 눈금이 겹친다 */
        tickFormatter={(v: string) => String(v).slice(5)}
      />
      <YAxis tick={{ fontSize: 10, fill: '#8c8c8c' }} width={46} domain={['auto', 'auto']} />
      <Tooltip
        formatter={((v: unknown) => [`${String(v)}${unit}`, s.label]) as never}
        labelStyle={{ fontSize: 12 }}
        contentStyle={{ fontSize: 12 }}
      />
      {/* 평균선 — 어느 날이 평소보다 높고 낮았는지가 한눈에 보인다 */}
      <ReferenceLine y={s.avg} stroke="#d9d9d9" strokeDasharray="4 4" />
      <Line
        type="monotone"
        dataKey="value"
        stroke={color}
        strokeWidth={2}
        dot={{ r: 2.5, fill: color }}
        isAnimationActive={false}
      />
    </LineChart>
  );

  return (
    <div className="eco-chart-card">
      <div className="eco-chart-head">
        <Text strong style={{ fontSize: 13 }}>
          {s.label}
        </Text>
        <Tag color={s.direction === 'flat' ? 'default' : s.direction === 'up' ? 'red' : 'blue'}>
          {arrow} {s.first}
          {unit} → {s.last}
          {unit}
          {s.changePct !== null && s.direction !== 'flat'
            ? ` (${s.changePct > 0 ? '+' : ''}${s.changePct}%)`
            : ''}
        </Tag>
      </div>
      <div className="eco-chart-body">
        {/*
          PDF 로 뜰 때는 너비를 못 박는다. ResponsiveContainer 는 부모 너비를
          재서 그리는데, 화면 밖에 숨겨 둔 사본은 그 너비가 0 이라 **빈 그림**이
          찍힌다. 캡처할 때만 숫자를 준다.
        */}
        <ResponsiveContainer width={fixedWidth ?? '100%'} height={150}>
          {chart}
        </ResponsiveContainer>
      </div>
      <div className="eco-chart-foot">
        최저 {s.min}
        {unit} · 최고 {s.max}
        {unit} · 평균 {s.avg}
        {unit} · {s.points.length}일
      </div>
    </div>
  );
}


const CSS = `
.eco-charts { display: flex; flex-direction: column; gap: 18px; }
.eco-chart-group-title {
  font-size: 13px;
  font-weight: 700;
  color: #262626;
  margin-bottom: 8px;
  padding-left: 8px;
  border-left: 3px solid #1677ff;
}
.eco-chart-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}
.eco-chart-card {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px 12px 8px;
  background: #fff;
}
.eco-chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
.eco-chart-foot {
  font-size: 11px;
  color: #8c8c8c;
  text-align: right;
  margin-top: 2px;
}

@media print {
  .eco-chart-row { grid-template-columns: repeat(2, 1fr); }
  .eco-chart-card { page-break-inside: avoid; border-color: #d9d9d9; }
}
`;
